import { dayLabel } from "@beacon/core/days"
import { formatOf, type DailyChartInput } from "./input.ts"

/**
 * A day's tooltip, from the day its focus is on: the day, then each series' value in the legend's
 * order, each with its colour, and the day's total; and last, on an annotated day, its annotations'
 * labels under `annotationsName`. Among several series, those that came to nothing that day are
 * left out; a single series stands alone, with no total to repeat it. A day whose history isn't
 * known says only that. The web app hands it to TanStack's native tooltip as `content`.
 */
export function tooltipOf<K extends string>(input: DailyChartInput<K>) {
  const format = formatOf(input)
  const { series, annotations = [], annotationsName = "Notes" } = input
  const byDay = new Map(input.days.map(({ day, values }) => [day, values]))
  const single = series.length === 1
  return (points: readonly { datum: { day: string } }[]) => {
    const day = points[0]?.datum.day ?? ""
    const values = byDay.get(day)
    if (!values) {
      const row = { label: "Not known", value: "", color: input.paints("muted") }
      return { title: dayLabel(day), rows: [row] }
    }
    const total = series.reduce((sum, { key }) => sum + values[key], 0)
    const noted = annotations.filter((annotation) => annotation.day === day)
    return {
      title: dayLabel(day),
      rows: [
        ...series
          .filter(({ key }) => single || values[key] !== 0)
          .map(({ key, label, paint }) => ({
            label,
            value: format(values[key]),
            color: input.paints(paint),
          })),
        ...(single ? [] : [{ label: "Total", value: format(total) }]),
        ...(noted.length > 0
          ? [{ label: annotationsName, value: noted.map(({ label }) => label).join(", ") }]
          : []),
      ],
    }
  }
}
